/**
 * SafeRichText — renders admin-authored TipTap HTML (preparationNotes, description)
 * as FORMATTED rich text, sanitized (booking-detail-reskin 6.3 / design D6).
 *
 * Fixes the legacy booking-detail bug where the HTML was shown as raw escaped
 * text. Sanitizes with a strict allowlist via the browser's own DOMParser (no
 * dependency) — parses to a detached document, walks the tree, drops any element
 * or attribute not on the allowlist, and keeps only http(s) link hrefs. Scripts,
 * event-handler attributes, styles, iframes, and images never reach the output.
 *
 * Allowlist: p, br, strong, b, em, i, u, s, ul, ol, li, a[href http(s)].
 */

import { useMemo } from 'react';
import styles from './SafeRichText.module.css';

const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'UL', 'OL', 'LI', 'A']);
/** Disallowed elements whose CONTENT is code/markup, not user text — drop entirely. */
const DROP_WITH_CONTENT = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG']);

const escapeText = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Plain text (no tags) as paragraphs (service-description-paragraphs): the service About editor
 * writes plain text, so a blank line starts a paragraph and a single line break stays a break.
 */
function plainTextToHtml(text: string): string {
  return text.replace(/\r\n?/g, '\n').trim().split(/\n\s*\n/)
    .map((paragraph) => `<p>${paragraph.split('\n').map(escapeText).join('<br>')}</p>`)
    .join('');
}

/** Sanitize a raw HTML string to the allowlist. SSR-safe (returns '' without a DOM). */
export function sanitizeRichText(html: string): string {
  if (!html) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return '';

  const source = /<\/?[a-z][^>]*>/i.test(html) ? html : plainTextToHtml(html);
  const doc = new DOMParser().parseFromString(source, 'text/html');

  const clean = (node: Node) => {
    // Iterate over a static copy — we mutate children as we go.
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as Element;
        if (DROP_WITH_CONTENT.has(el.tagName)) {
          // Code-bearing element (script/style/…): drop it AND its content.
          node.removeChild(el);
          continue;
        }
        if (!ALLOWED_TAGS.has(el.tagName)) {
          // Disallowed layout element: drop it but keep its (sanitized) text children inline.
          clean(el);
          while (el.firstChild) node.insertBefore(el.firstChild, el);
          node.removeChild(el);
          continue;
        }
        // Strip every attribute except a safe http(s) href on <a>.
        for (const attr of Array.from(el.attributes)) {
          const isSafeHref =
            el.tagName === 'A' &&
            attr.name === 'href' &&
            /^https?:\/\//i.test(attr.value.trim());
          if (!isSafeHref) el.removeAttribute(attr.name);
        }
        if (el.tagName === 'A') {
          el.setAttribute('target', '_blank');
          el.setAttribute('rel', 'noopener noreferrer');
        }
        clean(el);
      } else if (child.nodeType !== Node.TEXT_NODE) {
        // Comments / processing instructions — drop.
        node.removeChild(child);
      }
    }
  };

  clean(doc.body);
  return doc.body.innerHTML;
}

export interface SafeRichTextProps {
  html: string | null | undefined;
  className?: string;
}

/** Renders sanitized rich text; renders nothing when the (sanitized) content is empty. */
export function SafeRichText({ html, className }: SafeRichTextProps) {
  const clean = useMemo(() => sanitizeRichText(html ?? ''), [html]);
  if (!clean.trim()) return null;
  return (
    <div
      className={className ? `${styles.richText} ${className}` : styles.richText}
      // Content is allowlist-sanitized above (no script/style/iframe/img/handlers).
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
